import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConnectionServices } from '../authentication/connection/connection.service';
import { IdentityService } from '../authentication/identity.service';
import { NotificationsService } from '../notifications/notifications.service';
import {
  GetCarrierIntegrationsCommand,
  IGetCarrierIntegrationsParameters,
  IGetCarrierIntegrationsResponse,
  IntegrationItem
} from './command/getCarrierIntegrations.command';
import {
  GetCarrierIntegrationsWhatsappCommand,
  IGetCarrierIntegrationsWhatsappParameters,
  IGetCarrierIntegrationsWhatsappResponse
} from './command/getCarrierIntegrationsWhatsapp.command';
import {
  GetCarrierIntegrationsSignalCommand,
  IGetCarrierIntegrationsSignalParameters,
  IGetCarrierIntegrationsSignalResponse
} from './command/getCarrierIntegrationsSignal.command';
import {
  AddIntegrationCommand,
  IAddIntegrationParameters
} from './command/addIntegration.command';
import {
  ChangeIntegrationStatusCommand,
  IChangeIntegrationStatusParameters
} from './command/changeIntegrationStatus.command';
import {
  GetRolesCommand,
  IGetRolesParameters,
  IGetRolesResponse
} from './command/getRoles.command';
import {
  GetQRDataWhatsApp,
  IGetQRDataWhatsAppParameters,
  IGetQRDataWhatsAppResponse
} from './command/getQRDataWhatsApp';
import {
  GetCarrierPreferenceCommand,
  IGetCarrierPreferenceCommandParameters
} from './command/getCarrierPreference.command';

@Injectable()
export class IntegrationService {
  private titleMessage = 'toast.integrations.title';

  constructor(
    private _connectionService: ConnectionServices,
    private _identityService: IdentityService,
    private _notificationsService: NotificationsService
  ) {}

  public getCarrierIntegrations(): Observable<IGetCarrierIntegrationsResponse> {
    const command = new GetCarrierIntegrationsCommand();
    command.setParameters(<IGetCarrierIntegrationsParameters>{
      carrierUserId: this._identityService.userId
    });
    return this._connectionService.Request(command).pipe(
      map((response) => response as IGetCarrierIntegrationsResponse),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }

  public addIntegration(carrierAccountId: number): Observable<boolean> {
    const command = new AddIntegrationCommand();
    command.setParameters(<IAddIntegrationParameters>{
      carrierAccountId,
      contractorAccountId: null,
      applicationName: 'SMS',
      description: 'SMS API Integration',
      userName: 'SMS123',
      password: 'SMS123',
      enabled: true,
      blocked: false,
      securityRoleName: 'API_SMS'
    });
    return this._connectionService.Request(command).pipe(
      map(() => true),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }

  public changeIntegrationStatus(applicationId: number, status: boolean): Observable<boolean> {
    const command = new ChangeIntegrationStatusCommand();
    command.setParameters(<IChangeIntegrationStatusParameters>{
      applicationId,
      status
    });
    return this._connectionService.Request(command).pipe(
      map(() => true),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }

  // ===== WhatsApp-specific methods =====

  public getCarrierIntegrationsWhatsapp(
    carrierUserId: string,
    page: number,
    size: number,
    column: string,
    sort: string,
    find1: string | null,
    find2: string
  ): Observable<IGetCarrierIntegrationsWhatsappResponse> {
    const command = new GetCarrierIntegrationsWhatsappCommand();
    command.setParameters(<IGetCarrierIntegrationsWhatsappParameters>{
      carrierUserId,
      page,
      size,
      column,
      sort,
      find1,
      find2
    });
    return this._connectionService.Request(command).pipe(
      map((response) => response as IGetCarrierIntegrationsWhatsappResponse),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }

  public addWhatsAppIntegration(
    carrierAccountId: number,
    userName: string,
    password: string,
    securityRoleId: number
  ): Observable<boolean> {
    const command = new AddIntegrationCommand();
    command.setParameters(<IAddIntegrationParameters>{
      carrierAccountId,
      contractorAccountId: null,
      applicationName: 'AutoRespoder.WA',
      description: 'WhatsApp integration',
      userName,
      password,
      enabled: true,
      blocked: false,
      securityRoleName: 'API_WA'
    });
    return this._connectionService.Request(command).pipe(
      map(() => true),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }

  // ===== Signal-specific methods =====

  public getCarrierIntegrationsSignal(
    carrierUserId: string,
    page: number,
    size: number,
    column: string,
    sort: string,
    find1: string | null,
    find2: string
  ): Observable<IGetCarrierIntegrationsSignalResponse> {
    const command = new GetCarrierIntegrationsSignalCommand();
    command.setParameters(<IGetCarrierIntegrationsSignalParameters>{
      carrierUserId,
      page,
      size,
      column,
      sort,
      find1,
      find2
    });
    return this._connectionService.Request(command).pipe(
      map((response) => response as IGetCarrierIntegrationsSignalResponse),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }

  public addSignalIntegration(
    carrierAccountId: number,
    userName: string,
    password: string,
    securityRoleId: number
  ): Observable<boolean> {
    const command = new AddIntegrationCommand();
    command.setParameters(<IAddIntegrationParameters>{
      carrierAccountId,
      contractorAccountId: null,
      applicationName: 'AutoRespoder.SG',
      description: 'Signal integration',
      userName,
      password,
      enabled: true,
      blocked: false,
      securityRoleName: 'API_SG'
    });
    return this._connectionService.Request(command).pipe(
      map(() => true),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }

  // ===== Shared methods (WhatsApp + Signal) =====

  public getRoles(contractorId: string | null, apiRole: string): Observable<IGetRolesResponse[]> {
    const command = new GetRolesCommand();
    command.setParameters(<IGetRolesParameters>{
      contractorId,
      apiRole
    });
    return this._connectionService.Request(command).pipe(
      map((response) => response as unknown as IGetRolesResponse[]),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }

  public getCarrierPreferenceById(carrierUserId: string): Observable<any> {
    const command = new GetCarrierPreferenceCommand();
    command.setParameters(<IGetCarrierPreferenceCommandParameters>{
      carrierUserId
    });
    return this._connectionService.Request(command).pipe(
      map((response) => response),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }

  public getQRDataIntegration(
    username: string,
    password: string,
    idCarrier: string,
    lang: string,
    path: string,
    car?: string,
    option?: string,
    message?: string,
    selectOption?: string,
    messageHelp?: string,
    messageOther?: string,
    responderType?: string
  ): Observable<IGetQRDataWhatsAppResponse> {
    const command = new GetQRDataWhatsApp();
    command.setParameters(<IGetQRDataWhatsAppParameters>{
      username,
      password,
      idCarrier,
      lang,
      path,
      car,
      option,
      message,
      selectOption,
      messageHelp,
      messageOther,
      responderType
    });
    return this._connectionService.Request(command).pipe(
      map((response) => response as IGetQRDataWhatsAppResponse),
      catchError((error) => {
        this._notificationsService.showHttpErrorMessage(this.titleMessage, error);
        return throwError(() => error);
      })
    );
  }
}
